/**
 * Bot detection signals for Sentry event filtering
 *
 * Collects browser signals at module load time and provides a scoring
 * function to determine if the current session is likely a bot.
 * Used by sentry-config.js beforeSend to drop bot events silently.
 *
 * Scoring threshold: >= 80 points = bot
 *
 * Signal                                    | Points
 * ------------------------------------------|--------
 * navigator.webdriver                       | +80
 * navigator.languages empty                 | +40
 * Known bot UA substring                    | +90
 * Fake Chrome version (Chrome/X.0.0)        | +35
 * No plugins on non-mobile                  | +20
 * Screen 800x600 or 0x0                     | +25
 * colorDepth === 0                          | +15
 */

const BOT_UA_SUBSTRINGS = [
  'bot',
  'crawl',
  'spider',
  'headless',
  'phantom',
  'selenium',
  'puppeteer',
  'playwright',
  'slurp',
  'mediapartners',
  'lighthouse',
  'pagespeed',
  'googlebot',
  'bingbot',
  'yandexbot',
  'baiduspider',
  'facebookexternalhit',
  'twitterbot',
  'linkedinbot',
  'whatsapp',
  'applebot',
  'duckduckbot',
  'semrushbot',
  'ahrefsbot',
  'dotbot',
  'bytespider',
];

// Matches "Chrome/123.0.0" (3-segment with .0.0) — real Chrome uses 4-segment like "Chrome/123.0.6312.86"
const FAKE_CHROME_RE = /Chrome\/(\d+)\.0\.0(?:\s|$)/;

const isMobile = () => {
  try {
    return /Mobi|Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
  } catch {
    return false;
  }
};

/**
 * Collect bot signals from the browser environment.
 * Stores results on window.__csBot for one-time collection.
 * Safe to call multiple times — only runs once.
 */
export const collectBotSignals = () => {
  try {
    if (typeof window === 'undefined') return;
    if (window.__csBot) return; // Already collected

    const ua = (navigator.userAgent || '').toLowerCase();

    window.__csBot = {
      webdriver: !!navigator.webdriver,
      languagesEmpty: !navigator.languages || navigator.languages.length === 0,
      botUA: BOT_UA_SUBSTRINGS.some((sub) => ua.includes(sub)),
      fakeChromeVersion: FAKE_CHROME_RE.test(navigator.userAgent || ''),
      noPlugins: !isMobile() && (!navigator.plugins || navigator.plugins.length === 0),
      suspiciousScreen:
        (screen.width === 800 && screen.height === 600) ||
        (screen.width === 0 && screen.height === 0),
      zeroColorDepth: screen.colorDepth === 0,
    };
  } catch {
    // Non-critical — if collection fails, isBotLikely will return false
  }
};

/**
 * Score the current session and return true if score >= 80.
 * @returns {boolean}
 */
export const isBotLikely = () => {
  try {
    const signals = typeof window !== 'undefined' && window.__csBot;
    if (!signals) return false;

    let score = 0;

    if (signals.webdriver) score += 80;
    if (signals.languagesEmpty) score += 40;
    if (signals.botUA) score += 90;
    if (signals.fakeChromeVersion) score += 35;
    if (signals.noPlugins) score += 20;
    if (signals.suspiciousScreen) score += 25;
    if (signals.zeroColorDepth) score += 15;

    return score >= 80;
  } catch {
    return false;
  }
};
